import { useState, type ReactNode } from "react";
import type { Named, Uuid } from "@shared/api";
import { Dialog } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";
import { IconClose } from "@shared/ui/icons";
import type { Has } from "../lib/search-tokens";
import { NO_FILTERS, type Filters } from "../model/search";

export const HAS_LABEL: Record<Has, string> = { link: "ссылка", file: "вложение", image: "картинка" };

/** «Не важно» у выбора — свой знак: пустого значения Radix не принимает. */
const ANY = "any";

/**
 * Окно «Фильтры» — как «Больше фильтров» в поиске Discord: от кого, где, что
 * есть, кого зовут, когда, кем написано и закреплено ли. Правится копия:
 * «Отмена» ничего не трогает, «Применить» отдаёт всё разом и запускает поиск.
 */
export function FiltersDialog({
  people,
  channels,
  initial,
  onClose,
  onApply,
}: {
  people: Named[];
  channels: { id: Uuid; name: string }[];
  initial: Filters;
  onClose: () => void;
  onApply: (filters: Filters) => void;
}) {
  const [draft, setDraft] = useState<Filters>(initial);
  const [dates, setDates] = useState(!!(initial.after || initial.before));
  const nameOf = (person: Named) => person.display_name || person.username;

  return (
    <Dialog
      title="Фильтры"
      onClose={onClose}
      actions={
        <>
          <button type="button" className="filters-clear" onClick={() => setDraft(NO_FILTERS)}>
            Очистить фильтры
          </button>
          <span className="grow" />
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" onClick={() => onApply(draft)}>
            Применить фильтры
          </Button>
        </>
      }
    >
      <div className="filters-form">
        <Many
          label="От"
          hint="Отправлено любым из выбранных пользователей"
          placeholder={`Например, ${people[0] ? nameOf(people[0]) : "имя"}`}
          options={people.map((person) => ({ id: person.id, label: nameOf(person) }))}
          chosen={draft.authors}
          onChange={(authors) => setDraft({ ...draft, authors })}
        />
        {channels.length > 0 && (
          <Many
            label="В"
            hint="Отправлено в любом из выбранных каналов"
            placeholder={`Например, ${channels[0].name}`}
            options={channels.map((channel) => ({ id: channel.id, label: `#${channel.name}` }))}
            chosen={draft.channels}
            onChange={(next) => setDraft({ ...draft, channels: next })}
          />
        )}
        <Row label="Содержит" hint="Есть ли в сообщении ссылка, вложение или картинка">
          <Select value={draft.has ?? ANY} onValueChange={(value) => setDraft({ ...draft, has: value === ANY ? null : (value as Has) })}>
            <SelectTrigger aria-label="Содержит">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Любой контент</SelectItem>
              {(Object.keys(HAS_LABEL) as Has[]).map((value) => (
                <SelectItem key={value} value={value}>
                  {HAS_LABEL[value][0].toUpperCase() + HAS_LABEL[value].slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Row>
        <Many
          label="Упоминания"
          hint="Содержит упоминание любого из выбранных пользователей"
          placeholder={`Например, ${people[0] ? nameOf(people[0]) : "имя"}`}
          options={people.map((person) => ({ id: person.id, label: nameOf(person) }))}
          chosen={draft.mentions}
          onChange={(mentions) => setDraft({ ...draft, mentions })}
        />
        <Row label="Дата" hint="Поиск по дате отправки сообщения">
          {dates ? (
            <div className="filters-dates">
              <label>
                <span className="small muted">После</span>
                <Input
                  type="date"
                  value={draft.after ?? ""}
                  max={draft.before ?? undefined}
                  onChange={(event) => setDraft({ ...draft, after: event.target.value || null })}
                />
              </label>
              <label>
                <span className="small muted">До (включительно)</span>
                <Input
                  type="date"
                  value={draft.before ?? ""}
                  min={draft.after ?? undefined}
                  onChange={(event) => setDraft({ ...draft, before: event.target.value || null })}
                />
              </label>
            </div>
          ) : (
            <Button type="button" variant="secondary" onClick={() => setDates(true)}>
              + Добавить дату
            </Button>
          )}
        </Row>
        <Row label="Тип автора" hint="Кем отправлено: человеком или ботом">
          <Select
            value={draft.authorType ?? ANY}
            onValueChange={(value) => setDraft({ ...draft, authorType: value === ANY ? null : (value as "user" | "bot") })}
          >
            <SelectTrigger aria-label="Тип автора">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Любой</SelectItem>
              <SelectItem value="user">Человек</SelectItem>
              <SelectItem value="bot">Бот</SelectItem>
            </SelectContent>
          </Select>
        </Row>
        <Row label="Закреплено" hint="Закреплено ли сообщение">
          <Select
            value={draft.pinned === null ? ANY : draft.pinned ? "yes" : "no"}
            onValueChange={(value) => setDraft({ ...draft, pinned: value === ANY ? null : value === "yes" })}
          >
            <SelectTrigger aria-label="Закреплено">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Не важно</SelectItem>
              <SelectItem value="yes">Да</SelectItem>
              <SelectItem value="no">Нет</SelectItem>
            </SelectContent>
          </Select>
        </Row>
      </div>
    </Dialog>
  );
}

/**
 * Выбор нескольких — как в Discord: выпадающий список добавляет, выбранные —
 * плашками ниже, крестик убирает.
 */
function Many({
  label,
  hint,
  placeholder,
  options,
  chosen,
  onChange,
}: {
  label: string;
  hint: string;
  placeholder: string;
  options: { id: Uuid; label: string }[];
  chosen: Uuid[];
  onChange: (chosen: Uuid[]) => void;
}) {
  const left = options.filter((option) => !chosen.includes(option.id));
  return (
    <Row label={label} hint={hint}>
      <Select value="" onValueChange={(value) => value && onChange([...chosen, value])} disabled={left.length === 0}>
        <SelectTrigger aria-label={label}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {left.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {chosen.length > 0 && (
        <div className="filters-chips">
          {chosen.map((id) => (
            <span key={id} className="head-search-chip">
              <span className="ellipsis">{options.find((option) => option.id === id)?.label ?? "—"}</span>
              <button type="button" aria-label="Убрать" onClick={() => onChange(chosen.filter((item) => item !== id))}>
                <IconClose size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
    </Row>
  );
}

/** Поле окна — как в Discord: имя, под ним пояснение, ниже — сам выбор. */
function Row({ label, hint, children }: { label: string; hint: string; children: ReactNode }) {
  return (
    <div className="filters-row">
      <span className="filters-label">{label}</span>
      <span className="filters-hint">{hint}</span>
      {children}
    </div>
  );
}
